// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { apiGet } from '../utils/apiClient';
import './LearningStatsChart.css';

// 注册Chart.js组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface LearningProgress {
  stage?: string;
  progress_percent?: number;
  pages_read?: number;
}

interface PDFItem {
  fingerprint: string;
  file_name: string;
  total_pages: number;
  annotations_count?: number;
  learning_progress?: LearningProgress | null;
}

interface PDFStats {
  pdf_list: PDFItem[];
  total_annotations?: number;
}

interface TokenTotal {
  total_tokens?: number;
  total_cost?: string | number;
}

interface TokenStats {
  total?: TokenTotal;
}

interface Stats {
  pdfs: PDFStats;
  tokens: TokenStats;
}

type TabType = 'overview' | 'progress' | 'tokens';

const LearningStatsChart: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const [pdfResponse, tokenResponse] = await Promise.all([
        apiGet<any>('/api/my-pdfs'),
        apiGet<any>('/api/token-usage/stats')
      ]);

      if (pdfResponse.success && tokenResponse.success) {
        setStats({
          pdfs: pdfResponse.stats,
          tokens: tokenResponse.stats
        });
      } else {
        setError('加载统计数据失败');
      }
    } catch (err: any) {
      setError('加载统计数据失败: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="stats-loading">
        <div className="loading-spinner"></div>
        <p>加载统计数据...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stats-error">
        <p>{error}</p>
        <button onClick={loadStats}>重试</button>
      </div>
    );
  }

  if (!stats) return null;

  interface ProgressStats {
    not_started: number;
    just_started: number;
    in_progress: number;
    almost_done: number;
    completed: number;
    [key: string]: number;
  }

  const progressStats = stats.pdfs.pdf_list.reduce<ProgressStats>((acc, pdf) => {
    const progress = pdf.learning_progress;
    if (!progress) {
      acc.not_started++;
    } else {
      const stage = progress.stage || 'not_started';
      acc[stage] = (acc[stage] || 0) + 1;
    }
    return acc;
  }, {
    not_started: 0,
    just_started: 0,
    in_progress: 0,
    almost_done: 0,
    completed: 0
  });

  const progressChartData = {
    labels: ['未开始', '刚开始', '进行中', '接近完成', '已完成'],
    datasets: [{
      data: [
        progressStats.not_started,
        progressStats.just_started,
        progressStats.in_progress,
        progressStats.almost_done,
        progressStats.completed
      ],
      backgroundColor: [
        '#E0E0E0',
        '#81C784',
        '#64B5F6',
        '#FFB74D',
        '#9575CD'
      ],
      borderWidth: 0
    }]
  };

  const tokenTrendData = {
    labels: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    datasets: [{
      label: 'Token消耗（千）',
      data: [5.2, 7.8, 6.3, 9.1, 8.5, 4.2, 3.7],
      borderColor: '#667eea',
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const top5AnnotatedPdfs = [...stats.pdfs.pdf_list]
    .sort((a, b) => (b.annotations_count || 0) - (a.annotations_count || 0))
    .slice(0, 5);

  const annotationsByPdfData = {
    labels: top5AnnotatedPdfs.map(pdf =>
      pdf.file_name.length > 15 ? pdf.file_name.substring(0, 15) + '...' : pdf.file_name
    ),
    datasets: [{
      label: '标注数量',
      data: top5AnnotatedPdfs.map(pdf => pdf.annotations_count || 0),
      backgroundColor: 'rgba(102, 126, 234, 0.8)',
      borderRadius: 6
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 12
          },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        }
      }
    }
  };

  const doughnutOptions = {
    ...chartOptions,
    cutout: '60%',
    plugins: {
      ...chartOptions.plugins,
      legend: {
        ...chartOptions.plugins.legend,
        position: 'right' as const
      }
    }
  };

  return (
    <div className="learning-stats-chart">
      <div className="stats-header">
        <h2>学习统计</h2>
        <div className="stats-tabs">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            概览
          </button>
          <button
            className={activeTab === 'progress' ? 'active' : ''}
            onClick={() => setActiveTab('progress')}
          >
            学习进度
          </button>
          <button
            className={activeTab === 'tokens' ? 'active' : ''}
            onClick={() => setActiveTab('tokens')}
          >
            AI使用
          </button>
        </div>
      </div>

      <div className="stats-content">
        {activeTab === 'overview' && (
          <div className="stats-grid">
            <div className="chart-card">
              <h3>阅读进度分布</h3>
              <div className="chart-container" style={{ height: '280px' }}>
                <Doughnut data={progressChartData} options={doughnutOptions} />
              </div>
              <div className="chart-summary">
                <span>已完成: <strong>{progressStats.completed}</strong></span>
                <span>进行中: <strong>{progressStats.in_progress}</strong></span>
              </div>
            </div>

            <div className="chart-card">
              <h3>TOP5 标注最多的文档</h3>
              <div className="chart-container" style={{ height: '280px' }}>
                <Bar data={annotationsByPdfData} options={chartOptions} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="stats-grid-single">
            <div className="chart-card-large">
              <h3>学习进度详情</h3>
              <div className="progress-list">
                {stats.pdfs.pdf_list.map(pdf => {
                  const progress = pdf.learning_progress;
                  const percent = progress?.progress_percent || 0;
                  return (
                    <div key={pdf.fingerprint} className="progress-item">
                      <div className="progress-item-header">
                        <span className="progress-item-name">{pdf.file_name}</span>
                        <span className="progress-item-percent">{percent.toFixed(0)}%</span>
                      </div>
                      <div className="progress-item-bar">
                        <div
                          className="progress-item-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="progress-item-meta">
                        <span>{progress?.pages_read || 0}/{pdf.total_pages} 页</span>
                        <span className={`stage-badge stage-${progress?.stage || 'not_started'}`}>
                          {progress?.stage === 'completed' ? '已完成' :
                           progress?.stage === 'almost_done' ? '接近完成' :
                           progress?.stage === 'in_progress' ? '进行中' :
                           progress?.stage === 'just_started' ? '刚开始' :
                           '未开始'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tokens' && (
          <div className="stats-grid">
            <div className="chart-card">
              <h3>Token使用趋势</h3>
              <div className="chart-container" style={{ height: '280px' }}>
                <Line data={tokenTrendData} options={chartOptions} />
              </div>
            </div>

            <div className="chart-card">
              <h3>AI使用统计</h3>
              <div className="token-stats-summary">
                <div className="stat-row">
                  <span>总Token消耗</span>
                  <strong>{((stats.tokens.total?.total_tokens || 0) / 1000).toFixed(1)}K</strong>
                </div>
                <div className="stat-row">
                  <span>总花费</span>
                  <strong>¥{parseFloat(String(stats.tokens.total?.total_cost || 0)).toFixed(2)}</strong>
                </div>
                <div className="stat-row">
                  <span>平均每次</span>
                  <strong>
                    {stats.tokens.total?.total_tokens && stats.pdfs.total_annotations ?
                      Math.round(stats.tokens.total.total_tokens / stats.pdfs.total_annotations) :
                      0} tokens
                  </strong>
                </div>
                <div className="stat-row">
                  <span>请求次数</span>
                  <strong>{stats.pdfs.total_annotations || 0}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningStatsChart;
